import React, { useEffect, useState, useMemo, useContext, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import InnerLayout from "../../components/layouts/inner-layout";
import LoadingSpinner from "../../components/helper-components/loading-spinner";
import errorToast from "../../components/helper-components/error-toast";
import successToast from "../../components/helper-components/success-toast";
import ConfirmModal from "../../components/helper-components/confirm-modal";
import {
  getApprovalFlowListAction,
  updateApprovalFlowAction,
} from "../../redux/actions/approval-work-flow-action";
import { DataContext } from "../../components/layouts/main-layout";
import { FileText, TrendingUp } from "lucide-react";
import DynamicTransactionTable from "../../components/tables/dynamic-transaction-table";
import TransSummaryComp from "../../components/helper-components/trans-summary-comp";
import EditIcon, { VoidIcon } from "../../assets/icons/action-icons";
import EyeOpenIcon from "../../assets/icons/eye-open-icon";
import KibabMenu from "../../assets/icons/kibab-menu";

// The permission module this screen belongs to — the same module_name the
// sidebar entry uses. permissionFn matches on it exactly, so a wrong name
// silently hides every create/edit control.
const MODULE = "approval_workflow";

/**
 * Row actions. Kept as its own cell rather than the table's built-in ACTIONS
 * column: that one is driven by `<transaction>_order_status` and only ever
 * renders for purchase/sales documents, so an approval flow row came out blank.
 */
const FlowActions = ({ flow, canEdit, canDeactivate, onView, onEdit, onToggleActive }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isActive = flow?.is_active !== false;

  return (
    <div
      className="flex items-center gap-3"
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        title="View"
        className="flex items-center hover:opacity-70"
        onClick={() => onView(flow)}
      >
        <EyeOpenIcon fill="#98A2B3" />
      </button>

      {canEdit && (
        <button
          type="button"
          title="Edit"
          className="flex items-center hover:opacity-70"
          onClick={() => onEdit(flow)}
        >
          <EditIcon fill="#98A2B3" />
        </button>
      )}

      {canDeactivate && (
        <div ref={ref} className="relative">
          <button
            type="button"
            title="More"
            className="flex items-center hover:opacity-70"
            onClick={() => setOpen((prev) => !prev)}
          >
            <KibabMenu />
          </button>
          {open && (
            <div
              style={{
                minWidth: "11rem",
                zIndex: 999,
                top: "25px",
                right: "15px",
                boxShadow: "0px 1px 3px 0px var(--popover-shadow-color)",
              }}
              className="absolute border border-[var(--popover-border-color)] py-2 px-2 bg-[var(--popover-bg-color)] rounded flex flex-col"
            >
              <button
                type="button"
                onClick={() => {
                  onToggleActive(flow);
                  setOpen(false);
                }}
                className="flex items-center gap-2 text-[length:--popover-font-size] font-[var(--popover-font-weight)] text-[var(--popover-fg-color)] whitespace-nowrap cursor-pointer h-[35px] hover:bg-[var(--popover-hover-color)] rounded px-[8px]"
              >
                <VoidIcon fill="var(--popover-fg-color)" />
                {isActive ? "Deactivate" : "Activate"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Approval flow definitions — the workflows themselves, not the requests raised
 * against them. Rows open the view/edit screens.
 *
 * GET /approval-flows/list?pageSize=&pageNumber=&search=&is_active=
 */
const ApprovalList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { permissionFn } = useContext(DataContext);

  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dataCount, setDataCount] = useState(0);
  const [filterData, setFilterData] = useState({
    sort_by: "",
    sort_order: "",
    selectColumn: "",
    inputValue: "",
  });
  // The flow awaiting an activate/deactivate confirmation.
  const [confirmTarget, setConfirmTarget] = useState(null);

  /**
   * The approval-flows API has no delete route, so "removing" a flow means
   * flipping is_active through the same update endpoint the edit screen uses.
   * The row keeps its name and description so neither gets blanked.
   */
  const applyActiveChange = () => {
    const target = confirmTarget;
    if (!target) return;
    setConfirmTarget(null);
    setLoading(true);
    dispatch(
      updateApprovalFlowAction(
        {
          approvalFlowId: target.id,
          name: target.name,
          description: target.description,
          is_active: target.nextActive,
        },
        (data) => {
          successToast(
            data?.message ??
              (target.nextActive ? "Flow activated" : "Flow deactivated")
          );
          setReload((prev) => !prev);
        },
        (err) => {
          setLoading(false);
          errorToast(err?.data?.message ?? err?.message ?? "Something went wrong");
        }
      )
    );
  };

  useEffect(() => {
    setLoading(true);
    dispatch(
      getApprovalFlowListAction(
        {
          pageNumber: currentPage,
          pageSize: pageSize,
          // The endpoint searches name/description server-side.
          search: filterData.inputValue ?? "",
        },
        (data) => {
          // The response nests the rows a second level deep:
          //   { status, data: { totalCount, data: [ ...flows ] } }
          // so `data.data.data` holds the list. Other shapes are tolerated too.
          const payload = data?.data;
          const rows = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.data)
            ? payload.data
            : payload?.rows ?? payload?.list ?? [];
          setApiData(rows);
          setDataCount(
            Number(payload?.totalCount ?? payload?.total ?? rows.length) || 0
          );
          setLoading(false);
        },
        (err) => {
          setLoading(false);
          setApiData([]);
          errorToast(err?.message || "Failed to load approval flows");
        }
      )
    );
  }, [dispatch, reload, currentPage, pageSize, filterData]);

  const list = Array.isArray(apiData) ? apiData : [];

  const activeCount = list.filter((f) => f?.is_active !== false).length;
  const inactiveCount = list.length - activeCount;

  const summaryArray = (
    <div className="px-[24px] pt-[16px] flex flex-wrap gap-5">
      {[
        {
          conClass:
            "bg-[#e8f0fe] rounded-[12px] h-[5.3rem] flex-1 min-w-[240px] p-3 flex justify-between",
          label: "Total Flows",
          value: dataCount || list.length,
          desc: "Approval workflows",
          descClass: "text-xs font-medium text-[#4285f4]",
          icon: <FileText className="text-[#4285f4]" />,
        },
        {
          conClass:
            "bg-[#dcfce7] rounded-[12px] h-[5.3rem] flex-1 min-w-[240px] p-3 flex justify-between",
          label: "Active / Inactive",
          value: `${activeCount} / ${inactiveCount}`,
          desc: "On this page",
          descClass: "text-xs font-medium text-[#16a34a]",
          icon: <TrendingUp className="text-[#16a34a]" />,
        },
      ].map((sum, i) => (
        <TransSummaryComp key={i} sum={sum} />
      ))}
    </div>
  );

  const tableHeading = [
    { label: "FLOW NAME", value: "name", minWidth: 220, column: true, display: true, enabled: true },
    { label: "DESCRIPTION", value: "description", minWidth: 260, column: true, display: true, enabled: true },
    { label: "STATUS", value: "is_active", minWidth: 120, column: true, display: true, enabled: true },
    { label: "CREATED ON", value: "created_at", minWidth: 150, column: false, display: true, enabled: true },
    // Deliberately not labelled "ACTIONS": the table reserves that label (and the
    // value "actions") for its own order-status menu, which never renders for a
    // row without a <transaction>_order_status. "ACTION"/"row_actions" falls
    // through to the normal cell render, the same way User Creation does it.
    { label: "ACTION", value: "row_actions", minWidth: 130, column: false, display: false, enabled: true },
  ];

  // Search is applied by the API; this keeps the table's filter contract without
  // filtering a second time on the client.
  const handleFilter = () => list;

  const tableBodyData = useMemo(() => {
    const formatDate = (d) =>
      d
        ? new Date(d).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })
        : "-";

    return list.map((flow) => {
      const id = flow?.approval_flow_id ?? flow?.id;
      return {
        id,
        name: (
          <span
            className="text-[#4285f4] cursor-pointer hover:underline"
            onClick={() => navigate(`/approval-work-flow/view/${id}`)}
          >
            {flow?.name || "-"}
          </span>
        ),
        description: flow?.description || "-",
        is_active: (
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
              flow?.is_active !== false
                ? "bg-[#e5fbe9] text-[#5ca55e]"
                : "bg-gray-50 text-gray-600"
            }`}
          >
            {flow?.is_active !== false ? "Active" : "Inactive"}
          </span>
        ),
        created_at: formatDate(flow?.created_at),
        row_actions: (
          <FlowActions
            flow={flow}
            canEdit={Boolean(permissionFn(MODULE)?.update)}
            canDeactivate={Boolean(permissionFn(MODULE)?.delete)}
            onView={() => navigate(`/approval-work-flow/view/${id}`)}
            onEdit={() => navigate(`/approval-work-flow/edit/${id}`)}
            onToggleActive={() =>
              setConfirmTarget({
                id,
                name: flow?.name ?? "",
                description: flow?.description ?? "",
                nextActive: flow?.is_active === false,
              })
            }
          />
        ),
      };
    });
  }, [apiData, navigate, permissionFn]);

  const paginationProps = {
    pageSize: pageSize,
    setPageSize: setPageSize,
    dataCount: dataCount,
    setCurrentPage: setCurrentPage,
    currentPage: currentPage,
  };

  return (
    <InnerLayout title="Approval Work Flows" backBtn={true}>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <DynamicTransactionTable
          transaction="Approval Flow"
          transaction_type="Approval"
          tableHeading={tableHeading}
          is_checkbox_req={false}
          tableBodyData={tableBodyData}
          reload={reload}
          setReload={setReload}
          setLoading={setLoading}
          label="Flows"
          placeholder="Search flows..."
          summaryArray={summaryArray}
          paginationProps={paginationProps}
          onclkFn={() => navigate(`/approval-work-flow`)}
          permissionFn={permissionFn}
          module={MODULE}
          list={true}
          showFilter={true}
          filterSec={true}
          filterFn={handleFilter}
          setFilterData={setFilterData}
          filterData={filterData}
          date={false}
          path="approval-work-flow"
          invoiceItems={[]}
          setInvoiceItems={() => {}}
          orders={[]}
          setOrders={() => {}}
          showFilterArrows={false}
          onClickItemFn={(id) => navigate(`/approval-work-flow/view/${id}`)}
          canCreateNew={true}
          filter={false}
        />
      )}

      <ConfirmModal
        isOpen={Boolean(confirmTarget)}
        title={confirmTarget?.nextActive ? "Activate flow?" : "Deactivate flow?"}
        message={
          confirmTarget?.nextActive
            ? `"${confirmTarget?.name}" will be available for new approval requests again.`
            : `"${confirmTarget?.name}" will stop being used for new approval requests. You can activate it again later.`
        }
        confirmText={confirmTarget?.nextActive ? "Activate" : "Deactivate"}
        cancelText="Cancel"
        onConfirm={applyActiveChange}
        onCancel={() => setConfirmTarget(null)}
      />
    </InnerLayout>
  );
};
export default ApprovalList;
